// Data export service — G3.9 frontend client for /backups/exports/*.
// GDPR-compliant tenant data export (CSV / ZIP). Role OWNER (50)+.

import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DataExportStatus = 'QUEUED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export type DataExportCategory =
  | 'BILLING'
  | 'PRODUCTS'
  | 'INVENTORY'
  | 'LOCATIONS_USERS'
  | 'FINANCE_EXPENSES'
  | 'CRM'
  | 'LOYALTY'
  | 'CREDITS'
  | 'NOTIFICATIONS'
  | 'STAFF_SHIFTS'
  | 'APPROVALS_AUDIT'
  | 'ORDERS_RESTAURANT'
  | 'COMMUNICATIONS'
  | 'QUOTATIONS_PO'
  | 'FESTIVALS_BOOKINGS'
  | 'CORPORATE'
  | 'TERMINALS'
  | 'SUBSCRIPTIONS';

export interface DataExport {
  id: string;
  tenant_id: string;
  status: DataExportStatus;
  format: string;
  file_size_bytes: number | null;
  export_categories: DataExportCategory[] | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string | null;
}

export interface DataExportDetail extends DataExport {
  download_url?: string;
  expires_in?: number;
  error_message?: string;
}

export interface CreateDataExportPayload {
  categories?: DataExportCategory[];
}

export interface CreateDataExportResponse {
  export_id: string;
  status: string;
  message: string;
}

export interface DataExportListParams {
  page?: number;
  limit?: number;
  status?: DataExportStatus;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export interface DataExportListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  // Tenant-wide aggregates (whole history, not the current page). Used for the
  // "Storage Used" and "Last Export" KPI cards.
  total_size_bytes: number;
  last_completed_at: string | null;
  // Seconds remaining on the 1-per-24h export rate limit (0 when not limited).
  // Drives the "next export available in" countdown so it reflects the server's
  // real window and survives a page refresh.
  rate_limit_remaining_seconds: number;
}

export const dataExportService = {
  async list(params: DataExportListParams = {}): Promise<{
    data: DataExport[];
    meta: DataExportListMeta;
  }> {
    const q = buildQuery({ page: params.page, limit: params.limit ?? 20, status: params.status });
    const res = await apiClientPaginated<DataExport[]>(`/backups/exports${q}`);
    return { data: res.data, meta: res.meta as DataExportListMeta };
  },

  async getById(id: string): Promise<DataExportDetail> {
    return apiClient<DataExportDetail>(`/backups/exports/${id}`);
  },

  async request(payload: CreateDataExportPayload = {}): Promise<CreateDataExportResponse> {
    return apiClient<CreateDataExportResponse>('/backups/exports', {
      method: 'POST',
      body: payload,
    });
  },
};
